import React from 'react';

import {assert} from 'Asserts';
import {RegistryFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import {Permissions} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import {StringIDFolder} from 'constants/FolderConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {CreateDropdownOption, getFolderCreateDropdownOptions} from 'file-browser/CreateDropdownConstants';
import CreateBioentityModal from 'folder-items/CreateBioentityModal';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import normalizeFolder from 'folders/util/normalizeFolder';
import {ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import {useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {
  CreateMenuComplexPolymersQuery,
  CreateMenuComplexPolymersQueryVariables,
} from 'modbio/complex-polymers/__generated/CreateMenuComplexPolymersQuery';
import {
  useComplexPolymerSchemasAsCreateDropdownOptions,
  isComplexPolymerSchema,
  CREATE_MENU_COMPLEX_POLYMERS_QUERY,
  getComplexPolymerSchemaConfigs,
} from 'modbio/complex-polymers/ComplexPolymerSchemaUtil';
import useTagSchemaAwareObjectCreateQuery from 'nav/sidepanel/listing/hooks/useTagSchemaAwareObjectCreateQuery';
import {
  getProcessTaskSchemaContextualCreateOptions,
  useProcessTaskSchemasForCreate,
} from 'processes/CreateProcessUtil';
import * as CreateBioentityUtil from 'registry/create-bioentity/CreateBioentityUtil';
import * as RegistryUtil from 'registry/RegistryUtil';
import {SearchParams} from 'search/SearchConstants';
import {useStudySchemasAsCreateDropdownOptions} from 'studies/sidepanel/CreateStudyMenu';
import {toModelTypeConstant} from 'tag-schema/util/ModelTypeConversions';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {MenuHeight, MenuWidth, OptionData} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import {getRenderStarredOptionContent} from 'util/getRenderStarredOptionContent';
import sortedBy from 'util/sortedBy';

import {TagSchemaListingGraphQL} from '../../graphql-fragments/schemas/__generated/TagSchemaListingGraphQL';

type BfiOrMixture = ModelTypeConstants.BASIC_FOLDER_ITEM | ModelTypeConstants.MIXTURE;

function getPermissions(folder: StringIDFolder): Permissions | null {
  if (!folder || !folder.source) {
    return null;
  }
  assert(
    !RegistryUtil.isRegistry(folder.source),
    "Did not expect folder to be in the Registry. FolderCreateDropdown doesn't support the Registry yet."
  );
  let permissions;
  if (folder.permissions) {
    permissions = folder.permissions;
  }
  return permissions || null;
}

export function getTagSchemaMenuOptions(
  folderItemType: BfiOrMixture,
  tagSchemas: Array<TagSchemaListingGraphQL> | null,
  starredTagSchemaIds: Array<string> | null,
  schemaFilterFn?: (tagSchema: TagSchemaListingGraphQL) => boolean
): Array<CreateDropdownOption> {
  if (!tagSchemas) {
    return [];
  }
  let filteredSchemas = tagSchemas.filter(
    (tagSchema) =>
      toModelTypeConstant(tagSchema.folderItemType) === folderItemType &&
      tagSchema.permissions.objectsCreatable
  );
  if (schemaFilterFn) {
    filteredSchemas = filteredSchemas.filter((tagSchema) => schemaFilterFn(tagSchema));
  }
  const tagSchemaMenuOptions = filteredSchemas.map((tagSchema) => {
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const handleClick = () => {
      CreateBioentityModal.open(CreateBioentityModal, {
        createBioentityArgs: {
          ...CreateBioentityUtil.getDefaultCreateBioentityArgs(),
          tagSchema,
          folderId: FolderDefaults.get(FolderDefaultsKey.INVENTORY),
        },
      });
    };
    return {
      name: tagSchema.name,
      value: tagSchema.id,
      icon: tagSchema.iconId as BenchlingIconName,
      className: 'scrollable newDropdown-schemaOption',
      starrable: true,
      handleClick,
    };
  });
  if (!starredTagSchemaIds) {
    return tagSchemaMenuOptions;
  }
  return sortedBy(tagSchemaMenuOptions, [
    (option) => (starredTagSchemaIds.includes(option.value) ? 'a' : 'b'),
  ]);
}

function makeOptionData(option: CreateDropdownOption): OptionData {
  return {
    name: option.name,
    value: option.value,
    disabled: option.disabled,
    isHeader: option.isHeader,
    icon: option.icon,
    submenu: option.menu
      ? [option.menu.map((menuOption: CreateDropdownOption) => makeOptionData(menuOption))]
      : undefined,
    className: option.className,
    extra: {unstarrable: !option.starrable},
    dataTestAttrs: option.dataTestAttrs,
  };
}

function mapOptionValueToClickHandler(option: CreateDropdownOption): {[value: string]: () => void} {
  if (!option.handleClick) {
    return {};
  }
  return {
    [option.value]: () => {
      option.handleClick!();
    },
  };
}

function mapOptionValuesToClickHandlers(options: Array<CreateDropdownOption>): {[value: string]: () => void} {
  return options.reduce(
    (optionValuesToClickHandlers: {[value: string]: () => void}, option: CreateDropdownOption) => {
      const optionValueToClickHandler = mapOptionValueToClickHandler(option);
      const nestedOptionValuesToClickHandlers = option.menu
        ? mapOptionValuesToClickHandlers(option.menu)
        : {};
      return {
        ...optionValuesToClickHandlers,
        ...optionValueToClickHandler,
        ...nestedOptionValuesToClickHandlers,
      };
    },
    {}
  );
}

function makeOnSelectCallback(optionValuesToClickHandlers: {
  [value: string]: () => void;
}): (selectedItemValue: string) => void {
  return (selectedItemValue: string) => {
    assert(
      // eslint-disable-next-line benchlint/no-in-operator
      selectedItemValue in optionValuesToClickHandlers,
      `${selectedItemValue} has no associated click handler.`
    );
    optionValuesToClickHandlers[selectedItemValue]();
  };
}

export interface FolderCreateDropdownProps {
  dropLeft: boolean;
  folder: StringIDFolder;
  onOpenFolder: (folder: StringIDFolder, searchParams?: SearchParams) => void;
}

export interface FolderCreateDropdownEnhancedProps {
  folder: StringIDFolder;
  starredSchemaIds: Array<string> | null;
  toggleTagSchemaStar: (tagSchemaId: string) => void;
  tagSchemas: Array<TagSchemaListingGraphQL> | null;
}

export function FolderCreateDropdown(
  props: FolderCreateDropdownProps & FolderCreateDropdownEnhancedProps
): JSX.Element {
  const processTaskSchemas = useProcessTaskSchemasForCreate();
  const studySchemaCreateOptions = useStudySchemasAsCreateDropdownOptions(ActionOrigin.PROJECTS_SIDEPANEL, {
    initialFolderId: props.folder.id,
  });
  const complexPolymerCreateOptions = useComplexPolymerSchemasAsCreateDropdownOptions(props.folder.id);
  const complexPolymersSchemasResult = useSkippableBenchlingQuery<
    CreateMenuComplexPolymersQuery,
    CreateMenuComplexPolymersQueryVariables
  >(CREATE_MENU_COMPLEX_POLYMERS_QUERY, {
    fetchPolicy: 'cache-and-network',
    skip: !BenchlingConfig.MODBIO_ENABLE_COMPOSITE_ENTITIES,
  });
  const allComplexPolymerSchemaConfigs = getComplexPolymerSchemaConfigs(complexPolymersSchemasResult);
  const permissions = getPermissions(props.folder);
  const tagSchemas = props.tagSchemas || [];
  const options = getFolderCreateDropdownOptions(
    normalizeFolder(props.folder),
    permissions,
    props.onOpenFolder,
    getTagSchemaMenuOptions(
      ModelTypeConstants.BASIC_FOLDER_ITEM,
      tagSchemas,
      props.starredSchemaIds,
      (tagSchema: TagSchemaListingGraphQL) =>
        !isComplexPolymerSchema(allComplexPolymerSchemaConfigs, tagSchema.id)
    ),
    isFeatureEnabled({feature: RegistryFeature.REGISTRY_MIXTURES})
      ? getTagSchemaMenuOptions(ModelTypeConstants.MIXTURE, tagSchemas, props.starredSchemaIds)
      : [],
    getProcessTaskSchemaContextualCreateOptions(processTaskSchemas, props.folder.id),
    studySchemaCreateOptions,
    complexPolymerCreateOptions,
    allComplexPolymerSchemaConfigs
  );
  const optionValueToClickHandlers = mapOptionValuesToClickHandlers(options);
  const optionDatas = options.map((menuOption) => makeOptionData(menuOption));

  const menu = (
    <SearchableMenu
      onSelect={makeOnSelectCallback(optionValueToClickHandlers)}
      optionDatas={optionDatas}
      renderOptionContent={getRenderStarredOptionContent(props.toggleTagSchemaStar, props.starredSchemaIds, {
        getId: (optionData: OptionData) => optionData.value,
      })}
      height={MenuHeight.LARGE}
      width={MenuWidth.LARGE}
      dropLeft={props.dropLeft}
    />
  );

  return (
    <Dropdown closeOnInsideClick={true} closeOnOutsideClick={true} menu={menu}>
      <Button
        className="u-mini-spacing-left"
        buttonStyle={ButtonStyle.TERTIARY_BLUE}
        icon="compose"
        size={ButtonSize.SMALL}
        disabled={!permissions || !permissions.appendable}
      />
    </Dropdown>
  );
}

function LoadingIconButton({icon}: {icon: BenchlingIconName}): JSX.Element {
  // Should resemble the dropdown button in FolderCreateDropdown, but disabled
  return (
    <Tooltip text="Loading..." disableContentSelection={true}>
      <Button
        className="u-mini-spacing-left"
        buttonStyle={ButtonStyle.TERTIARY_BLUE}
        icon={icon}
        size={ButtonSize.SMALL}
        disabled={true}
      />
    </Tooltip>
  );
}

export default function QueryingFolderCreateDropdown(props: FolderCreateDropdownProps): JSX.Element | null {
  const {folder, ...restProps} = props;
  const result = useTagSchemaAwareObjectCreateQuery(folder);

  if (result == null) {
    return <LoadingIconButton icon="compose" />;
  } else {
    const {tagSchemas, starredTagSchemaIds, handleToggleTagSchemaStar, folder: modifiedFolder} = result;

    return (
      <FolderCreateDropdown
        {...restProps}
        starredSchemaIds={starredTagSchemaIds}
        toggleTagSchemaStar={handleToggleTagSchemaStar}
        tagSchemas={tagSchemas}
        folder={modifiedFolder}
      />
    );
  }
}
